import { categoryFor } from '@trips/shared/balances';
import { newId } from '@trips/shared/ids';
import { perkDiff, withHistory } from '@trips/shared/perks';
import type { Benefit, Card, Perk, Program, Redemption } from '@trips/shared/schema';
import { BENEFIT_PERIODS, PERK_KINDS } from '@trips/shared/schema-consts';
import { PROGRAMS, programInfo } from '@trips/shared/valuations';
import { type ReactNode, useState } from 'react';
import { put, remove } from '../../data/mutate';
import { selectToday, selectWallet } from '../../data/selectors';
import { getState, useStore } from '../../data/store';
import { navigate } from '../../lib/router';
import { Button } from '../../ui/Button';
import { FieldRow, Select, TextArea, TextField, Toggle } from '../../ui/Field';
import { Sheet } from '../../ui/Sheet';
import s from './WalletEditor.module.css';

export type WalletKind = 'choose' | 'program' | 'card' | 'perk' | 'bonus' | 'redemption';

type Writable<T> = Omit<T, 'rev' | 'updatedAt' | 'createdAt' | 'deleted'>;

const PROGRAM_OPTIONS = [
  { value: '', label: 'Choose a program' },
  ...PROGRAMS.map((p) => ({ value: p.id, label: p.name })),
  { value: 'other', label: 'Other' },
];

const PERIOD_LABELS: Record<Benefit['period'], string> = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  semiannual: 'Twice a year',
  annual: 'Calendar year',
  'cardmember-year': 'Card year',
  once: 'Once',
  ongoing: 'Always on',
};

const PERK_LABELS: Record<Perk['kind'], string> = {
  credit: 'Credit',
  certificate: 'Free night or certificate',
  bonus: 'Bonus to earn',
  upgrade: 'Upgrade',
  voucher: 'Voucher or travel credit',
  other: 'Other',
};

const num = (v: string) => (v.trim() === '' ? null : Number(v.replace(/[$,\s]/g, '')));
const str = (v: number | null | undefined) => (v == null ? '' : String(v));
const date = (v: string) => (v ? v : null);

function Form({
  children,
  onSave,
  onDelete,
  error,
}: {
  children: ReactNode;
  onSave: () => void;
  onDelete?: () => void;
  error: string | null;
}) {
  return (
    <form
      className={s.form}
      onSubmit={(e) => {
        e.preventDefault();
        onSave();
      }}
    >
      {children}
      {error && <p className={s.error}>{error}</p>}
      <div className={s.buttons}>
        {onDelete && (
          <Button variant="ghost" icon="trash" onClick={onDelete}>
            Delete
          </Button>
        )}
        <Button type="submit" variant="primary">
          Save
        </Button>
      </div>
    </form>
  );
}

function ProgramForm({ record, done }: { record?: Program; done: () => void }) {
  const today = useStore(selectToday);
  const [programId, setProgramId] = useState(record?.programId ?? '');
  const [name, setName] = useState(record?.name ?? '');
  const [balance, setBalance] = useState(str(record?.balance));
  const [tier, setTier] = useState(record?.tier ?? '');
  const [expires, setExpires] = useState(record?.expiresOn ?? '');
  const [notes, setNotes] = useState(record?.notes ?? '');
  const [error, setError] = useState<string | null>(null);
  const save = () => {
    const b = num(balance);
    const label = programInfo(programId)?.name ?? name.trim();
    if (!programId || !label) return setError('Choose the program.');
    if (b == null || Number.isNaN(b) || b < 0) return setError('Enter the balance.');
    const value: Writable<Program> = {
      // Keeps what the form doesn't show (last activity, expiry note, wallet tab).
      ...record,
      id: record?.id ?? newId('prg'),
      programId,
      name: label,
      balance: Math.round(b),
      tier: tier.trim() || null,
      expiresOn: date(expires),
      checkedOn: today,
      notes,
      category: record && record.programId === programId ? record.category : categoryFor(programId),
    };
    // A changed balance is activity, which moves an expiry date out.
    if (record && Math.round(b) !== record.balance) value.lastActivityOn = today;
    void put('programs', value);
    done();
  };
  return (
    <Form
      onSave={save}
      error={error}
      onDelete={record ? () => void remove('programs', record.id).then(done) : undefined}
    >
      <Select label="Program" value={programId} onChange={setProgramId} options={PROGRAM_OPTIONS} />
      {programId === 'other' && <TextField label="Name" value={name} onChange={setName} />}
      <FieldRow>
        <TextField label="Balance" value={balance} onChange={setBalance} inputMode="numeric" />
        <TextField label="Status" value={tier} onChange={setTier} placeholder="Globalist" />
      </FieldRow>
      <TextField label="Points expire" type="date" value={expires} onChange={setExpires} />
      <TextArea label="Notes" value={notes} onChange={setNotes} minRows={2} />
    </Form>
  );
}

function BenefitRows({ list, onChange }: { list: Benefit[]; onChange: (b: Benefit[]) => void }) {
  const set = (i: number, patch: Partial<Benefit>) =>
    onChange(list.map((b, j) => (j === i ? { ...b, ...patch } : b)));
  return (
    <div className={s.benefits}>
      <p className={s.label}>Benefits and credits</p>
      {list.map((b, i) => (
        <div key={b.id} className={s.benefit}>
          <TextField
            label="Benefit"
            value={b.title}
            onChange={(v) => set(i, { title: v })}
            placeholder="Airline fee credit"
          />
          <FieldRow>
            <TextField
              label="Value, $"
              value={str(b.value)}
              onChange={(v) => set(i, { value: num(v) })}
              inputMode="decimal"
            />
            <Select
              label="Every"
              value={b.period}
              onChange={(v) => set(i, { period: v as Benefit['period'] })}
              options={BENEFIT_PERIODS.map((p) => ({ value: p, label: PERIOD_LABELS[p] }))}
            />
          </FieldRow>
          <FieldRow>
            <TextField
              label="Used, $"
              value={str(b.used)}
              onChange={(v) => set(i, { used: num(v) })}
              inputMode="decimal"
            />
            <TextField
              label="Resets"
              type="date"
              value={b.resetsOn ?? ''}
              onChange={(v) => set(i, { resetsOn: date(v) })}
            />
          </FieldRow>
          <Button
            variant="ghost"
            icon="trash"
            onClick={() => onChange(list.filter((_, j) => j !== i))}
          >
            Remove benefit
          </Button>
        </div>
      ))}
      <Button
        icon="plus"
        onClick={() =>
          onChange([
            ...list,
            {
              id: Math.random().toString(36).slice(2, 10),
              title: '',
              value: null,
              period: 'annual',
              resetsOn: null,
              used: null,
              note: null,
            },
          ])
        }
      >
        Add benefit
      </Button>
    </div>
  );
}

function CardForm({ record, done }: { record?: Card; done: () => void }) {
  const [name, setName] = useState(record?.name ?? '');
  const [issuer, setIssuer] = useState(record?.issuer ?? '');
  const [last4, setLast4] = useState(record?.last4 ?? '');
  const [fee, setFee] = useState(str(record?.annualFee));
  const [due, setDue] = useState(record?.feeDueOn ?? '');
  const [opened, setOpened] = useState(record?.openedOn ?? '');
  const [earns, setEarns] = useState(record?.earns ?? '');
  const [benefits, setBenefits] = useState<Benefit[]>(record?.benefits ?? []);
  const [notes, setNotes] = useState(record?.notes ?? '');
  const [error, setError] = useState<string | null>(null);
  const save = () => {
    if (!name.trim()) return setError('Add the card name.');
    if (last4 && !/^\d{4}$/.test(last4)) return setError('Only the last 4 digits, please.');
    const f = num(fee) ?? 0;
    const value: Writable<Card> = {
      id: record?.id ?? newId('crd'),
      name: name.trim(),
      issuer: issuer.trim(),
      last4: last4 || null,
      annualFee: Number.isNaN(f) ? 0 : f,
      feeDueOn: date(due),
      openedOn: date(opened),
      earns: earns || null,
      benefits: benefits.filter((b) => b.title.trim()),
      notes,
    };
    void put('cards', value);
    done();
  };
  return (
    <Form
      onSave={save}
      error={error}
      onDelete={record ? () => void remove('cards', record.id).then(done) : undefined}
    >
      <TextField label="Card" value={name} onChange={setName} placeholder="Sapphire Reserve" />
      <FieldRow>
        <TextField label="Issuer" value={issuer} onChange={setIssuer} placeholder="Chase" />
        <TextField
          label="Last 4"
          value={last4}
          onChange={(v) => setLast4(v.replace(/\D/g, '').slice(0, 4))}
          inputMode="numeric"
        />
      </FieldRow>
      <FieldRow>
        <TextField label="Annual fee, $" value={fee} onChange={setFee} inputMode="decimal" />
        <TextField label="Fee due" type="date" value={due} onChange={setDue} />
      </FieldRow>
      <FieldRow>
        <Select
          label="Earns"
          value={earns}
          onChange={setEarns}
          options={[{ value: '', label: 'Cash back or none' }, ...PROGRAM_OPTIONS.slice(1, -1)]}
        />
        <TextField label="Opened" type="date" value={opened} onChange={setOpened} />
      </FieldRow>
      <BenefitRows list={benefits} onChange={setBenefits} />
      <TextArea label="Notes" value={notes} onChange={setNotes} minRows={2} />
    </Form>
  );
}

function PerkForm({ record, bonus, done }: { record?: Perk; bonus?: boolean; done: () => void }) {
  const cards = useStore(selectWallet).cards;
  const [kind, setKind] = useState<Perk['kind']>(record?.kind ?? (bonus ? 'bonus' : 'credit'));
  const [title, setTitle] = useState(record?.title ?? '');
  const [programId, setProgramId] = useState(record?.programId ?? '');
  const [cardId, setCardId] = useState(record?.cardId ?? '');
  const [value, setValue] = useState(str(record?.value));
  const [done_, setDone] = useState(str(record?.progress?.done));
  const [target, setTarget] = useState(str(record?.progress?.target));
  const [expires, setExpires] = useState(record?.expiresOn ?? '');
  const [used, setUsed] = useState(record?.used ?? false);
  const [quantity, setQuantity] = useState(String(record?.quantity ?? 1));
  const [notes, setNotes] = useState(record?.notes ?? '');
  const [error, setError] = useState<string | null>(null);
  const isBonus = kind === 'bonus';
  const save = () => {
    if (!title.trim()) return setError('Add what it is.');
    const t = num(target);
    const q = Math.max(0, Math.round(num(quantity) ?? 1));
    const status: Perk['status'] = used || q === 0 ? 'used' : 'active';
    const record_: Writable<Perk> = {
      // Keep what this form doesn't show (uses, links, history).
      ...(record ?? {}),
      id: record?.id ?? newId('prk'),
      kind,
      title: title.trim(),
      programId: programId || null,
      cardId: cardId || null,
      value: num(value),
      unit: num(value) == null ? null : isBonus ? 'points' : 'usd',
      progress: isBonus && t ? { done: num(done_) ?? 0, target: t } : null,
      expiresOn: date(expires),
      used: status === 'used',
      notes,
      quantity: q,
      status,
    };
    const changes = perkDiff(record ?? {}, record_ as Perk);
    record_.history = withHistory(
      (record ?? {}) as Perk,
      record ? 'edit' : 'add',
      changes,
      Date.now(),
    );
    void put('perks', record_);
    done();
  };
  return (
    <Form
      onSave={save}
      error={error}
      onDelete={record ? () => void remove('perks', record.id).then(done) : undefined}
    >
      <Select
        label="Kind"
        value={kind}
        onChange={(v) => setKind(v as Perk['kind'])}
        options={PERK_KINDS.map((k) => ({ value: k, label: PERK_LABELS[k] }))}
      />
      <TextField
        label="What"
        value={title}
        onChange={setTitle}
        placeholder={isBonus ? 'Welcome bonus' : 'Hyatt free night, category 1 to 4'}
      />
      <FieldRow>
        <Select
          label="Program"
          value={programId}
          onChange={setProgramId}
          options={[{ value: '', label: 'None' }, ...PROGRAM_OPTIONS.slice(1, -1)]}
        />
        <Select
          label="Card"
          value={cardId}
          onChange={setCardId}
          options={[
            { value: '', label: 'None' },
            ...cards.map((c) => ({ value: c.id, label: c.name })),
          ]}
        />
      </FieldRow>
      <TextField
        label={isBonus ? 'Bonus points' : 'Value, $'}
        value={value}
        onChange={setValue}
        inputMode="decimal"
      />
      {isBonus && (
        <FieldRow>
          <TextField label="Spent so far, $" value={done_} onChange={setDone} inputMode="decimal" />
          <TextField
            label="Spend needed, $"
            value={target}
            onChange={setTarget}
            inputMode="decimal"
          />
        </FieldRow>
      )}
      <FieldRow>
        <TextField label="Use by" type="date" value={expires} onChange={setExpires} />
        <TextField label="How many" value={quantity} onChange={setQuantity} inputMode="numeric" />
      </FieldRow>
      <Toggle label="Used" checked={used} onChange={setUsed} />
      <TextArea label="Notes" value={notes} onChange={setNotes} minRows={2} />
    </Form>
  );
}

function RedemptionForm({
  record,
  programId: initial,
  done,
}: {
  record?: Redemption;
  programId?: string | null;
  done: () => void;
}) {
  const today = useStore(selectToday);
  const [programId, setProgramId] = useState(record?.programId ?? initial ?? '');
  const [on, setOn] = useState(record?.on ?? today);
  const [points, setPoints] = useState(str(record?.points));
  const [cash, setCash] = useState(str(record?.cashValue));
  const [fees, setFees] = useState(str(record?.fees));
  const [title, setTitle] = useState(record?.title ?? '');
  const [notes, setNotes] = useState(record?.notes ?? '');
  const [error, setError] = useState<string | null>(null);
  const save = () => {
    const p = num(points);
    if (!programId) return setError('Choose the program.');
    if (!title.trim()) return setError('Add what you got.');
    if (p == null || Number.isNaN(p)) return setError('Enter the points used.');
    const value: Writable<Redemption> = {
      id: record?.id ?? newId('rdm'),
      programId,
      on,
      points: Math.round(p),
      cashValue: num(cash),
      fees: num(fees),
      title: title.trim(),
      tripId: record?.tripId ?? null,
      notes,
    };
    void put('redemptions', value);
    done();
  };
  return (
    <Form
      onSave={save}
      error={error}
      onDelete={record ? () => void remove('redemptions', record.id).then(done) : undefined}
    >
      <Select
        label="Program"
        value={programId}
        onChange={setProgramId}
        options={PROGRAM_OPTIONS.slice(0, -1)}
      />
      <TextField
        label="What you got"
        value={title}
        onChange={setTitle}
        placeholder="Park Hyatt Tokyo, 2 nights"
      />
      <FieldRow>
        <TextField label="Points" value={points} onChange={setPoints} inputMode="numeric" />
        <TextField label="Date" type="date" value={on} onChange={setOn} />
      </FieldRow>
      <FieldRow>
        <TextField label="Cash price, $" value={cash} onChange={setCash} inputMode="decimal" />
        <TextField label="Taxes and fees, $" value={fees} onChange={setFees} inputMode="decimal" />
      </FieldRow>
      <TextArea label="Notes" value={notes} onChange={setNotes} minRows={2} />
    </Form>
  );
}

const CHOICES: { kind: Exclude<WalletKind, 'choose'>; title: string; detail: string }[] = [
  { kind: 'program', title: 'Points balance', detail: 'A loyalty program and how many points' },
  { kind: 'card', title: 'Credit card', detail: 'Fee, renewal date and benefits' },
  { kind: 'perk', title: 'Credit or certificate', detail: 'Free nights, credits, vouchers' },
  { kind: 'bonus', title: 'Bonus to earn', detail: 'A spending bonus and how far along it is' },
  { kind: 'redemption', title: 'Redemption', detail: 'Points you spent and what you got' },
];

const TITLES: Record<WalletKind, string> = {
  choose: 'Add to wallet',
  program: 'Points',
  card: 'Card',
  perk: 'Credit or certificate',
  bonus: 'Bonus',
  redemption: 'Redemption',
};

export function WalletEditor({
  open,
  onClose,
  kind,
  recordId,
  programId,
}: {
  open: boolean;
  onClose: () => void;
  kind: WalletKind;
  recordId?: string;
  programId?: string | null;
}) {
  const t = getState().tables;
  const done = onClose;
  let body: ReactNode = null;
  if (kind === 'choose')
    body = (
      <div className={s.choices}>
        {CHOICES.map((c) => (
          <button
            key={c.kind}
            type="button"
            className={s.choice}
            onClick={() => navigate(`/wallet/new/${c.kind}`, { overlay: true, replace: true })}
          >
            <span className={s.choiceTitle}>{c.title}</span>
            <span className={s.choiceDetail}>{c.detail}</span>
          </button>
        ))}
      </div>
    );
  else if (kind === 'program')
    body = <ProgramForm record={recordId ? t.programs.get(recordId) : undefined} done={done} />;
  else if (kind === 'card')
    body = <CardForm record={recordId ? t.cards.get(recordId) : undefined} done={done} />;
  else if (kind === 'perk' || kind === 'bonus')
    body = (
      <PerkForm
        record={recordId ? t.perks.get(recordId) : undefined}
        bonus={kind === 'bonus'}
        done={done}
      />
    );
  else if (kind === 'redemption')
    body = (
      <RedemptionForm
        record={recordId ? t.redemptions.get(recordId) : undefined}
        programId={programId}
        done={done}
      />
    );
  return (
    <Sheet open={open} onClose={onClose} title={TITLES[kind]} size="full">
      {body}
    </Sheet>
  );
}
